import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Shield, Building2, Phone, Mail, FileCheck, Edit2 } from 'lucide-react';
import { NotificationPreferences } from './profile/NotificationPreferences';
import { ContactInformation } from './profile/ContactInformation';

interface InsurerProfile {
  id: string;
  name: string;
  trading_name?: string;
  registration_number: string;
  fca_number: string;
  vat_number?: string;
  registered_address: string;
  operating_address?: string;
  website_url: string;
  primary_contact_name: string;
  job_title: string;
  primary_email: string;
  phone_number: string;
  claims_email: string;
  support_email: string;
  emergency_phone: string;
  insurance_products: string[];
  underwriter_info: string;
  status: 'pending' | 'active' | 'rejected';
  created_at: string;
  notification_preferences: {
    newRequests: boolean;
    losingBids: boolean;
    winningBids: boolean;
    clientApproval: boolean;
    emailNotifications: boolean;
    inAppNotifications: boolean;
  };
}

export function InsurerProfile() {
  const [profile, setProfile] = useState<InsurerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState<InsurerProfile | null>(null);

  useEffect(() => {
    fetchProfile();
  }, []);
  
  async function fetchProfile() {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('Not authenticated');
      }

      const { data: profile, error } = await supabase
        .from('insurers')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      
      // Ensure insurance_products is always an array
      const processedProfile = {
        ...profile,
        insurance_products: profile.insurance_products || []
      };
      
      setProfile(processedProfile);
      setEditedProfile(processedProfile);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load profile';
      setError(errorMessage);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }

  const handleNotificationPreferencesUpdate = async (field: string, value: boolean) => {
    if (!editedProfile) return;
    
    try {
      const updatedPreferences = {
        ...editedProfile.notification_preferences,
        [field]: value
      };

      const { error } = await supabase.rpc(
        'update_notification_preferences',
        { 
          p_insurer_id: editedProfile.id,
          p_preferences: updatedPreferences
        }
      );

      if (error) throw error;

      setEditedProfile(prev => prev ? {
        ...prev,
        notification_preferences: updatedPreferences
      } : null);
      
      setProfile(prev => prev ? {
        ...prev,
        notification_preferences: updatedPreferences
      } : null);

    } catch (err) {
      setError('Failed to update notification preferences');
      console.error('Error updating preferences:', err);
    }
  };

  const handleSave = async () => {
    if (!editedProfile) return;
    
    try {
      const { error } = await supabase
        .from('insurers')
        .update(editedProfile)
        .eq('id', editedProfile.id);

      if (error) throw error;
      
      setProfile(editedProfile);
      setIsEditing(false);
      alert('Profile updated successfully!');
    } catch (err) {
      setError('Failed to update profile');
      console.error('Error updating profile:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Profile Found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Please complete your registration to access the insurer dashboard.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-indigo-600" />
              <h2 className="ml-3 text-xl font-bold text-gray-900">Company Profile</h2>
            </div>
            <div className="flex items-center space-x-4">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                profile.status === 'active' ? 'bg-green-100 text-green-800' :
                profile.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {profile.status.charAt(0).toUpperCase() + profile.status.slice(1)}
              </span>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900"
              >
                <Edit2 className="h-4 w-4 mr-1" />
                {isEditing ? 'Cancel Editing' : 'Edit Profile'}
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 space-y-8">
          {/* Company Information */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
              <Building2 className="h-5 w-5 text-indigo-600 mr-2" />
              Company Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500">Company Name</label>
                <p className="mt-1 text-sm text-gray-900">{profile.name}</p>
              </div>
              {profile.trading_name && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Trading Name</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.trading_name}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-500">Registration Number</label>
                <p className="mt-1 text-sm text-gray-900">{profile.registration_number}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">FCA Number</label>
                <p className="mt-1 text-sm text-gray-900">{profile.fca_number}</p>
              </div>
              {profile.vat_number && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">VAT Number</label>
                  <p className="mt-1 text-sm text-gray-900">{profile.vat_number}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-500">Website</label>
                <a
                  href={profile.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 text-sm text-indigo-600 hover:text-indigo-500"
                >
                  {profile.website_url}
                </a>
              </div>
            </div>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Registered Address</label>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">
                  {profile.registered_address}
                </p>
              </div>
              {profile.operating_address && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Operating Address</label>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">
                    {profile.operating_address}
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Contact Information */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
              <Phone className="h-5 w-5 text-indigo-600 mr-2" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500">Primary Contact</label>
                <p className="mt-1 text-sm text-gray-900">{profile.primary_contact_name}</p>
                <p className="text-sm text-gray-500">{profile.job_title}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Primary Email</label>
                <p className="mt-1 text-sm text-gray-900">{profile.primary_email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Phone Number</label>
                <p className="mt-1 text-sm text-gray-900">{profile.phone_number}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Emergency Phone</label>
                <p className="mt-1 text-sm text-gray-900">{profile.emergency_phone}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Claims Email</label>
                <p className="mt-1 text-sm text-gray-900">{profile.claims_email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Support Email</label>
                <p className="mt-1 text-sm text-gray-900">{profile.support_email}</p>
              </div>
            </div>
          </section>

          {/* Insurance Products */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
              <Shield className="h-5 w-5 text-indigo-600 mr-2" />
              Insurance Products & Services
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">
                  Products Offered
                </label>
                <div className="flex flex-wrap gap-2">
                  {profile.insurance_products.map((product) => (
                    <span
                      key={product}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                    >
                      {product}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Underwriter Information
                </label>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">
                  {profile.underwriter_info}
                </p>
              </div>
            </div>
          </section>

          {/* Contact Information */}
          <ContactInformation
            data={{
              primaryContactName: profile.primary_contact_name,
              jobTitle: profile.job_title,
              primaryEmail: profile.primary_email,
              phoneNumber: profile.phone_number,
              claimsEmail: profile.claims_email,
              supportEmail: profile.support_email,
              emergencyPhone: profile.emergency_phone
            }}
            isEditing={isEditing}
            onUpdate={(field, value) => {
              if (editedProfile) {
                setEditedProfile({
                  ...editedProfile,
                  [field]: value
                });
              }
            }}
          />

          {/* Notification Preferences */}
          <NotificationPreferences
            preferences={profile.notification_preferences}
            onUpdate={handleNotificationPreferencesUpdate}
          />

          {/* Account Status */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
              <FileCheck className="h-5 w-5 text-indigo-600 mr-2" />
              Account Status
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Status</label>
                  <p className={`mt-1 text-sm font-medium ${
                    profile.status === 'active' ? 'text-green-600' :
                    profile.status === 'pending' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {profile.status.charAt(0).toUpperCase() + profile.status.slice(1)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Member Since</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {new Date(profile.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              {profile.status === 'pending' && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-100 rounded">
                  <p className="text-sm text-yellow-700">
                    Your account is pending approval. We will review your application and contact you soon.
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}